/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

@import url('../shared.css');

:host {
  display: inline-block;
}

@-moz-document url-prefix() {
  [type='date'],
  [type='datetime-local'],
  [type='month'],
  [type='time'],
  [type='week'] {
    ~ .qds-icon-container {
      display: none;
    }
  }
}

.qds-container,
.qds-input {
  width: inherit;
}

.qds-container {
  align-content: flex-start;
  align-items: center;
  box-sizing: border-box;
  display: grid;
  grid-template-areas: 'input';
}

.qds-icon {
  --qds-theme-interactive-background-hover: none;
  --qds-theme-interactive-background-pressed: none;
  --qds-control-small-height: var(--qds-control-small-icon-size);

  flex-shrink: 0;
}

.qds-input,
.qds-icon-container {
  grid-area: input;
}

.qds-icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: end;
  padding-inline-end: var(--qds-control-input-padding-horizontal);

  &.no-padding {
    padding-inline-end: 0;
  }
}

.qds-focus-ring {
  border-radius: max(
      1px,
      var(
        --qds-control-border-radius-top-left,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-top-right,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-bottom-right,
        var(--qds-control-border-radius)
      )
    )
    max(
      1px,
      var(
        --qds-control-border-radius-bottom-left,
        var(--qds-control-border-radius)
      )
    );
  grid-area: input;
  isolation: isolate;
  outline: var(--qds-control-input-focus-border-width) solid
    var(--qds-theme-control-input-focus-border);
  pointer-events: none;
  visibility: hidden;
  width: calc(
    100% + var(--qds-control-border-width) -
      var(
        --qds-control-border-width-inline-end,
        var(--qds-control-border-width)
      )
  );
  height: calc(
    100% + var(--qds-control-border-width) -
      var(--qds-control-border-width-block-end, var(--qds-control-border-width))
  );
}

[data-size='small'] {
  font: var(--qds-control-small-text);
  height: var(--qds-control-small-height);

  &.qds-icon {
    --qds-experimental-icon-click-target-size: var(--qds-control-small-height);

    width: var(--qds-control-small-icon-size);
    height: var(--qds-control-small-icon-size);
  }

  & ~ .qds-icon-container {
    gap: var(--qds-control-small-gap-internal);
    margin-inline-start: var(--qds-control-small-gap-internal);
  }

  &[type='date'],
  &[type='datetime-local'],
  &[type='email'],
  &[type='month'],
  &[type='time'],
  &[type='url'],
  &[type='tel'],
  &[type='week'] {
    padding-inline: var(--qds-control-input-padding-horizontal)
      calc(
        var(--qds-control-input-padding-horizontal) +
          var(--qds-control-small-height)
      );
  }

  &[type='file']::file-selector-button {
    font: var(--qds-control-small-text);
    height: var(--qds-control-small-height);
    margin-inline-end: var(--qds-control-small-gap-siblings-related);
    padding-inline: var(--qds-control-button-small-padding-horizontal);
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    margin-inline-start: var(--qds-control-small-gap-internal);
  }

  &[type='search'] {
    padding-inline: var(--qds-control-input-padding-horizontal)
      calc(
        var(--qds-control-small-height) + var(--qds-control-small-icon-size) +
          var(--qds-control-input-padding-horizontal) +
          var(--qds-control-small-gap-internal)
      );
  }
}

[data-size='standard'] {
  font: var(--qds-control-standard-text);
  height: var(--qds-control-standard-height);

  &.qds-icon {
    --qds-experimental-icon-click-target-size: var(
      --qds-control-standard-height
    );

    width: var(--qds-control-standard-icon-size);
    height: var(--qds-control-standard-icon-size);
  }

  & ~ .qds-icon-container {
    gap: var(--qds-control-standard-gap-internal);
    margin-inline-start: var(--qds-control-standard-gap-internal);
  }

  &[type='date'],
  &[type='datetime-local'],
  &[type='email'],
  &[type='month'],
  &[type='time'],
  &[type='url'],
  &[type='tel'],
  &[type='week'] {
    padding-inline: var(--qds-control-input-padding-horizontal)
      calc(
        var(--qds-control-input-padding-horizontal) +
          var(--qds-control-standard-height)
      );
  }

  &[type='file']::file-selector-button {
    font: var(--qds-control-standard-text);
    height: var(--qds-control-standard-height);
    margin-inline-end: var(--qds-control-standard-gap-siblings-related);
    padding-inline: var(--qds-control-button-standard-padding-horizontal);
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    margin-inline-start: var(--qds-control-standard-gap-internal);
  }

  &[type='search'] {
    padding-inline: var(--qds-control-input-padding-horizontal)
      calc(
        var(--qds-control-small-height) + var(--qds-control-standard-icon-size) +
          var(--qds-control-input-padding-horizontal) +
          var(--qds-control-standard-gap-internal)
      );
  }
}

[data-size='large'] {
  font: var(--qds-control-large-text);
  height: var(--qds-control-large-height);

  &.qds-icon {
    --qds-experimental-icon-click-target-size: var(--qds-control-large-height);

    width: var(--qds-control-large-icon-size);
    height: var(--qds-control-large-icon-size);
  }

  & ~ .qds-icon-container {
    gap: var(--qds-control-large-gap-internal);
    margin-inline-start: var(--qds-control-large-gap-internal);
  }

  &[type='date'],
  &[type='datetime-local'],
  &[type='email'],
  &[type='month'],
  &[type='time'],
  &[type='url'],
  &[type='tel'],
  &[type='week'] {
    padding-inline: var(--qds-control-input-padding-horizontal)
      calc(
        var(--qds-control-input-padding-horizontal) +
          var(--qds-control-large-height)
      );
  }

  &[type='file']::file-selector-button {
    font: var(--qds-control-large-text);
    height: var(--qds-control-large-height);
    margin-inline-end: var(--qds-control-large-gap-siblings-related);
    padding-inline: var(--qds-control-button-large-padding-horizontal);
  }

  &[type='number'] {
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      margin-inline-start: var(--qds-control-large-gap-internal);
    }
  }

  &[type='search'] {
    padding-inline: var(--qds-control-input-padding-horizontal)
      calc(
        var(--qds-control-small-height) + var(--qds-control-large-icon-size) +
          var(--qds-control-input-padding-horizontal) +
          var(--qds-control-large-gap-internal)
      );
  }
}

.qds-input {
  appearance: textfield;
  background-color: var(--qds-theme-control-input-background);
  border-color: var(--qds-theme-control-border);
  border-radius: var(
      --qds-control-border-radius-top-left,
      var(--qds-control-border-radius)
    )
    var(--qds-control-border-radius-top-right, var(--qds-control-border-radius))
    var(
      --qds-control-border-radius-bottom-right,
      var(--qds-control-border-radius)
    )
    var(
      --qds-control-border-radius-bottom-left,
      var(--qds-control-border-radius)
    );
  border-style: solid;
  border-width: var(--qds-control-border-width)
    var(--qds-control-border-width-inline-end, var(--qds-control-border-width))
    var(--qds-control-border-width-block-end, var(--qds-control-border-width))
    var(--qds-control-border-width);
  box-sizing: border-box;
  color: var(--qds-theme-control-text-standard);
  grid-area: input;
  padding-block: 0;
  padding-inline: var(--qds-control-input-padding-horizontal);

  &.qds-hide ~ .qds-icon-container {
    display: none;
  }

  &:disabled {
    opacity: var(--qds-theme-disabled);
    pointer-events: none;
  }

  &:focus-visible {
    outline: none;

    ~ .qds-focus-ring {
      visibility: visible;
    }
  }

  &:placeholder-shown {
    color: var(--qds-theme-control-text-placeholder);
  }

  &:not(:focus)[aria-invalid='true'] {
    border-color: var(--qds-theme-feedback-result-failure);
  }

  &:is(
      [type='date'],
      [type='datetime-local'],
      [type='month'],
      [type='time'],
      [type='week']
    )::-webkit-calendar-picker-indicator,
  &::-webkit-search-cancel-button {
    cursor: pointer;
    display: none;
  }

  &[type='file'] {
    padding-block: var(--qds-control-input-padding-horizontal);

    &::file-selector-button {
      background-color: transparent;
      border: var(--qds-control-border-width) solid
        var(--qds-theme-control-border);
      border-radius: var(--qds-control-border-radius);
      color: var(--qds-theme-control-text-standard);

      &:hover {
        background-color: var(--qds-theme-interactive-background-hover);
      }

      &:active {
        background-color: var(--qds-theme-interactive-background-pressed);
      }
    }
  }

  &[type='text'],
  &[type='password'],
  &[type='color'],
  &[type='number'] {
    padding-inline: var(--qds-control-input-padding-horizontal);
  }

  &[type='color'] {
    appearance: none;
    min-width: 50px;
    padding-block: var(--qds-control-input-padding-horizontal);

    &::-webkit-color-swatch-wrapper {
      padding: 0;
    }

    &::-webkit-color-swatch {
      border: var(--qds-control-border-width) solid
        var(--qds-theme-control-border);
      border-radius: var(--qds-control-border-radius);
    }

    &::-moz-color-swatch {
      border: var(--qds-control-border-width) solid
        var(--qds-theme-control-border);
      border-radius: var(--qds-control-border-radius);
    }

    cursor: pointer;

    &:hover {
      background-color: var(--qds-theme-interactive-background-hover);
    }

    &:active {
      background-color: var(--qds-theme-interactive-background-pressed);
    }
  }

  &[data-size='small'],
  &[data-size='standard'],
  &[data-size='large'] {
    &.qds-search-padding {
      padding-inline: var(--qds-control-input-padding-horizontal)
        calc(
          var(--qds-control-small-height) +
            var(--qds-control-input-padding-horizontal)
        );
    }

    &[type='file'] {
      height: initial;
    }

    &.qds-padding {
      padding-inline: var(--qds-control-input-padding-horizontal);
    }

    @-moz-document url-prefix() {
      &[type='date'],
      &[type='datetime-local'],
      &[type='month'],
      &[type='week'],
      &[type='time'] {
        padding-inline-end: var(--qds-control-input-padding-horizontal);
      }
    }
  }

  @-moz-document url-prefix() {
    &[type='number'] {
      appearance: none;
    }
  }
}
